import {
    Container,
    Header,
    HeaderActions,
    VersionSelectorContainer,
} from './ResultsDisplay.styles';
import {
    ContentDisplay,
    useAppSelector,
    Button,
    IconButton,
    Icon,
    FormSelector,
    IOption,
    Tooltip,
} from '../../../../base';
import { toolsSelectors } from '../../../../actions';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    show: boolean;
    setShow: (show: boolean) => void;
}

const ResultsDisplay = ({ show, setShow }: Props) => {
    const { t } = useTranslation();
    const results = useAppSelector(
        toolsSelectors.getActiveChatGeneratedContent
    );
    const { control, watch, setValue } = useForm<{ version: IOption }>({
        defaultValues: {
            version: {
                label: `v${results.length > 0 ? results.length - 1 : 0}`,
                value: results.length > 0 ? String(results.length - 1) : '0',
            },
        },
    });

    const selectedVersionIndex = watch('version');

    const [currentContent, setCurrentContent] = useState<string | undefined>(
        ''
    );

    useEffect(() => {
        if (results && results.length > 0) {
            setValue('version', {
                label: `v${results.length}`,
                value: String(results.length - 1),
            });
        }
    }, [results, setValue]);

    useEffect(() => {
        if (results && results[Number(selectedVersionIndex.value)]) {
            setCurrentContent(
                results[Number(selectedVersionIndex.value)]?.message
            );
        } else if (results && results.length > 0) {
            setCurrentContent(results[results.length - 1]?.message);
        } else {
            setCurrentContent('');
        }
    }, [results, selectedVersionIndex]);

    if (!show) {
        return null;
    }

    const handleCopy = () => {
        if (currentContent) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = currentContent;
            const plainText = tempDiv.textContent || tempDiv.innerText || '';

            if (navigator.clipboard && window.ClipboardItem) {
                const htmlBlob = new Blob([currentContent], {
                    type: 'text/html',
                });
                const textBlob = new Blob([plainText], { type: 'text/plain' });
                const item = new ClipboardItem({
                    'text/html': htmlBlob,
                    'text/plain': textBlob,
                });
                navigator.clipboard.write([item]).catch((err) => {
                    console.error('Failed to copy rich text.', err);
                    navigator.clipboard.writeText(plainText);
                });
            } else {
                navigator.clipboard.writeText(plainText);
            }
        }
    };

    const handlePublish = () => {
        console.log('Publishing content:', currentContent);
    };

    const versionOptions: Array<IOption> =
        results.length > 0
            ? results.map((_, index) => ({
                  label: `v${index + 1}`,
                  value: index.toString(),
              }))
            : [];

    return (
        <Container>
            <Header>
                <HeaderActions>
                    <Tooltip title={t('tools.comingSoon')}>
                        <div>
                            <Button
                                variant="contained"
                                startIcon={
                                    <Icon name="ShareOutlinedIcon" size={20} />
                                }
                                onClick={handlePublish}
                                disabled
                            >
                                {t('tools.chat.publish')}
                            </Button>
                        </div>
                    </Tooltip>
                    <IconButton
                        onClick={handleCopy}
                        disabled={!currentContent}
                        aria-label="Copy content"
                    >
                        <Icon name="CopyIcon" />
                    </IconButton>
                    {results && results.length > 0 && (
                        <VersionSelectorContainer>
                            <FormSelector
                                name="version"
                                control={control}
                                options={versionOptions}
                                disableClearable
                            />
                        </VersionSelectorContainer>
                    )}
                </HeaderActions>
                <IconButton
                    onClick={() => setShow(false)}
                    aria-label="Close results display"
                >
                    <Icon name="CloseIcon" />
                </IconButton>
            </Header>
            <ContentDisplay content={currentContent || ''} />
        </Container>
    );
};

export default ResultsDisplay;
